import React from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import classnames from 'classnames';
import noop from 'lodash/noop';
import Portal from 'react-overlays/es/Portal';

import IconButton from '@adsk/bim360-matrix-react-icon-button';
import Carousel from '@adsk/bim360-matrix-react-carousel';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Loader from '@adsk/bim360-matrix-react-loader';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import { KeyCodesUppercase } from '@adsk/bim360-matrix-react-helpers/es/consts';

import modalClose from '@adsk/bim360-matrix-react-assets/modal-close.svg';
import imageError from '@adsk/bim360-matrix-react-assets/image-error-icn.svg';

import './_Gallery.scss';

export class Gallery extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedItem: props.selectedItem,
      errorItems: props.items.reduce((errorItems, item) => {
        if (item.error) {
          errorItems[item.original] = true;
        }
        return errorItems;
      }, {}),
      imageHeight: '100%'
    };

    this._handleKeyboard = this._handleKeyboard.bind(this);
    this._handleResize = this._handleResize.bind(this);
    this.close = this.close.bind(this);
    this.triggerCalcHeight = this.triggerCalcHeight.bind(this);
  }

  _handleKeyboard(event) {
    switch (this.props.keyboard && event.keyCode) {
      case KeyCodesUppercase.ESCAPE:
        this.close();
        event.stopPropagation();
        break;
      case KeyCodesUppercase.ARROW_LEFT:
        this.carousel.prev();
        break;
      case KeyCodesUppercase.ARROW_RIGHT:
        this.carousel.next();
        break;
      default:
        break;
    }
  }

  /**
   * We're handling the resize of the window so the image will scale according to window size.
   * @private
   */
  _handleResize() {
    this.triggerCalcHeight();
  }

  close() {
    // We're timeouting the actual hide to occur after the animation has finished.
    this.setState({ exited: true }, () =>
      setTimeout(() => {
        this.props.onHide();
        this.props.onExited();
      }, 500)
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      (nextProps.selectedItem !== this.props.selectedItem &&
        nextProps.selectedItem !== this.state.selectedItem) ||
      !nextProps.items[this.state.selectedItem]
    ) {
      this.setState({
        selectedItem: Math.min(Math.max(nextProps.selectedItem, 0), nextProps.items.length)
      });
    }
  }

  componentDidMount() {
    this.setState({ entered: true });
    window.addEventListener('keyup', this._handleKeyboard);
    window.addEventListener('resize', this._handleResize);
    this.triggerCalcHeight();
  }

  componentWillUnmount() {
    window.removeEventListener('keyup', this._handleKeyboard);
    window.removeEventListener('resize', this._handleResize);
    this.props.onExit();
  }

  _renderActionToolbar() {
    const self = this;
    const selectedItem = self.props.items[self.state.selectedItem];
    const toolbarActions = selectedItem.toolbarActions || this.props.toolbarActions || [];
    return (
      <div className="Gallery__action-toolbar">
        <IconButton
          className="Gallery__close-button"
          data-testid="Gallery__close-button"
          onClick={this.close.bind(this)}
        >
          <SvgIcon width="20px" height="20px">
            {modalClose}
          </SvgIcon>
        </IconButton>
        {toolbarActions.map((toolbarAction, index) => {
          const isGalleryActionToolbar = !!toolbarAction.props.isAvailable;
          const elementToUse = isGalleryActionToolbar
            ? toolbarAction.props.children
            : toolbarAction;
          return !isGalleryActionToolbar || toolbarAction.props.isAvailable(selectedItem)
            ? React.cloneElement(elementToUse, {
                key: index,
                onClick() {
                  elementToUse.props.onClick(selectedItem, self.state.selectedItem);
                }
              })
            : null;
        })}
      </div>
    );
  }

  triggerCalcHeight() {
    const { galleryRef } = this;
    if (galleryRef) {
      const carouselArrowWidth = 78;
      const galleryPadding = 30;
      const imageWidth =
        galleryRef.clientWidth - galleryPadding * 2 - carouselArrowWidth * 2 + 'px';
      // 30 is because of the padding of the entire gallery, 100 is because of the `calc` in the _Gallery.scss
      this.setState({
        imageHeight: galleryRef.clientHeight - 30 * 2 - 100 + 'px',
        imageWidth
      });
    }
  }

  _renderImage(item, index) {
    const { fitImage } = this.props;
    const currentImageContainerStyle = {
      height: this.state.imageHeight,
      width: this.state.imageWidth
    };

    const containerClasses = classnames('Gallery__current-image-container', {
      'Gallery__current-image-container--error': this.state.errorItems[item.original]
    });

    if (this.state.errorItems[item.original]) {
      return (
        <div key={index} className={containerClasses}>
          <SvgIcon width="35px" height="35px" className="">
            {imageError}
          </SvgIcon>
          <span>{this.props[TSymbolProp].translate('IMAGE_LOAD_ERROR')}</span>
        </div>
      );
    }

    if (item.isLoading) {
      return (
        <div key={`${index}-loading`} className={containerClasses}>
          <Loader />
        </div>
      );
    }

    return (
      <div key={index} className={containerClasses}>
        <img
          style={currentImageContainerStyle}
          className="Gallery__current-image"
          data-testid="Gallery__current-image"
          onLoad={item.onLoad}
          onError={() =>
            this.setState({ errorItems: { ...this.state.errorItems, [item.original]: true } })
          }
          src={item.original}
        />
      </div>
    );
  }

  render() {
    const carouselSettings = {
      slide: this.state.selectedItem,
      className: 'Gallery__carousel',
      beforeChange: (currentItem, nextItem) => {
        this.setState({ selectedItem: nextItem });
      }
    };
    const galleryClasses = classnames('Gallery', this.props.className, {
      'Gallery--entered': this.state.entered,
      'Gallery--exited': this.state.exited
    });

    return (
      <Portal container={this.props.container}>
        <div ref={ref => (this.galleryRef = ref)} className={galleryClasses}>
          <div className="Gallery__top-bar">
            <span className="Gallery__current-image-name" data-testid="Gallery__current-image-name">
              {this.props.items[this.state.selectedItem].name}
            </span>
            {this._renderActionToolbar()}
          </div>
          {this.props.items.length > 0 && (
            <Carousel {...carouselSettings} ref={s => (this.carousel = s)}>
              {this.props.items.map(this._renderImage.bind(this))}
            </Carousel>
          )}
        </div>
      </Portal>
    );
  }
}

Gallery.defaultProps = {
  selectedItem: 0,
  keyboard: true,
  onExit: noop,
  onExited: noop
};

Gallery.propTypes = {
  /**
   * an optional className to append to the main div of the component
   */
  className: PropTypes.string,
  /**
   * The items to render in the gallery
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The original sized image to ge displayed as a full-bleed when currently viewed.
       * If error or isLoading is passed then this property is not required.
       */
      original: PropTypes.string,
      /**
       * The thumbnail of the image, should be smaller in size to save bandwidth
       */
      thumbnail: PropTypes.string,
      /**
       * Custom onLoad function for that given item
       */
      onLoad: PropTypes.func,
      /**
       * The name of the image, shown in the top left area
       */
      name: PropTypes.string.isRequired,
      /**
       * Whether or not to show an error placeholder
       */
      error: PropTypes.bool,
      /**
       * Whether or not to show the loading placeholder
       */
      isLoading: PropTypes.bool,
      /**
       * Actions to be rendered on the gallery toolbar, each action has an onClick handler that get's called with the current item being shown
       */
      toolbarActions: PropTypes.arrayOf(PropTypes.node)
    })
  ).isRequired,
  /**
   * Function that's called when hiding the gallery
   */
  onHide: PropTypes.func.isRequired,
  /**
   * Function that's called when hiding the gallery
   */
  onExited: PropTypes.func,
  /**
   * Function that's called when the gallery unmounts
   */
  onExit: PropTypes.func,
  /**
   * A container object to render the gallery on as a modal
   */
  container: PropTypes.object,
  /**
   * Actions to be rendered on the gallery toolbar, each action has an onClick handler that get's called with the current item being shown
   */
  toolbarActions: PropTypes.arrayOf(PropTypes.node),
  /**
   * The selected index of the item to show
   */
  selectedItem: PropTypes.number,
  /**
   * Whether or not keyboard control should be supported when using the arrow keys
   */
  keyboard: PropTypes.bool,
  /**
   * The locale for the texts in the component
   */
  locale: PropTypes.string
};

export default withLocale(Gallery, ({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
